import { getStationDisplayProps } from "../../utils/getStationState";
import { formatTime } from "./format";
import { MyPlace, MyStation } from "./types";

/**
 * `Ф1` (кабинет ПЗС): правила показа раздела «Станции».
 *
 * Отдельно от компонента, чтобы проверялось без браузера: какие места попадают
 * в список, что делать с местом без станции, закрытой связью и станцией не на
 * связи. Статус станции берётся у `getStationDisplayProps` — порядок
 * приоритетов тот же, что на сайте персонала; `status_id` собирается из `online`
 * (в DTO кабинета его нет).
 */

export const STATIONS_REFRESH_MS = 15000;

export type PlaceViewKind = "station" | "waiting-act" | "pending" | "closed";

export type PlaceView = {
  key: string;
  placeId: number;
  number: string | null;
  plantName: string | null;
  place: MyPlace | null;
  station: MyStation | null;
  kind: PlaceViewKind;
};

/** Состояние станции словами; приоритеты — как в `getStationDisplayProps`. */
export function stationStatusOf(station: MyStation): {
  label: string;
  hint: string | null;
  color: string;
} {
  const display = getStationDisplayProps({
    status_id: station.online === false ? 0 : 1,
    state_id: station.state_id ?? 0,
    gps: station.error?.active ? station.error.code ?? "ошибка" : null,
    last_session: station.last_session,
  });

  // 1. Нет связи с модулем
  if (station.state_id === 6) {
    return { label: "Нет связи с модулем", hint: null, color: display.color };
  }

  // 2. Станция не на связи — с какого времени нет данных
  if (station.online === false) {
    return {
      label: `Нет связи со станцией с ${formatTime(station.last_data_at)}`,
      hint: null,
      color: display.color,
    };
  }

  // 3. Ошибка станции (код из `error`, без служебного текста модуля)
  if (station.error?.active) {
    return { label: "Ошибка станции", hint: null, color: display.color };
  }

  return { label: display.state, hint: display.hint, color: display.color };
}

/** Места и станции → карточки: станция, ждёт акта, связь ещё не началась, закрыта. */
export function buildPlaceViews(
  stations: MyStation[],
  places: MyPlace[],
): PlaceView[] {
  const stationByPlaceId = new Map<number, MyStation>();

  for (const station of stations) {
    if (station.place?.id !== null && station.place?.id !== undefined) {
      stationByPlaceId.set(Number(station.place.id), station);
    }
  }

  const views: PlaceView[] = [];

  for (const place of places) {
    const placeId =
      place.place?.id !== null && place.place?.id !== undefined
        ? Number(place.place.id)
        : null;

    if (placeId === null) continue;

    const station = stationByPlaceId.get(placeId) ?? null;
    stationByPlaceId.delete(placeId);

    const kind: PlaceViewKind =
      place.state === "closed" || place.state === "cancelled"
        ? "closed"
        : place.state === "pending"
          ? "pending"
          : station
            ? "station"
            : "waiting-act";

    views.push({
      key: `place-${placeId}`,
      placeId,
      number: place.place?.number ?? null,
      plantName: place.place?.plant?.name ?? null,
      place,
      station,
      kind,
    });
  }

  // Станция без строки связи в `/my/places` (сегодня не бывает, но ответы двух
  // маршрутов могут разойтись по времени) — карточка остаётся, не теряется.
  for (const [placeId, station] of stationByPlaceId) {
    views.push({
      key: `place-${placeId}`,
      placeId,
      number: station.place?.number ?? null,
      plantName: station.plant?.name ?? null,
      place: null,
      station,
      kind: "station",
    });
  }

  return views.sort((left, right) =>
    String(left.number ?? "").localeCompare(String(right.number ?? ""), "ru", {
      numeric: true,
    }),
  );
}

/** Русская форма слова по числу: 1 место, 2 места, 5 мест. */
function plural(count: number, one: string, few: string, many: string): string {
  const tail = count % 100;

  if (tail >= 11 && tail <= 14) return many;

  switch (count % 10) {
    case 1:
      return one;
    case 2:
    case 3:
    case 4:
      return few;
    default:
      return many;
  }
}

/** «3 места на 2 объектах» — для подзаголовка раздела. */
export function placesSummaryOf(views: PlaceView[]): string {
  const places = views.length;
  const plants = new Set(
    views.map((view) => view.plantName).filter((name) => Boolean(name)),
  ).size;

  return `${places} ${plural(places, "место", "места", "мест")} на ${plants} ${plural(
    plants,
    "объекте",
    "объектах",
    "объектах",
  )}`;
}
